import { useDispatch, useSelector } from 'react-redux';
import {
  CHANGED_MAP_PATHS_MAP,
  CM_IN_ONE_INCHES,
  EMISSION_NEW_MAX,
  GENERATE_MAP_TYPE,
  TEXTURE_MAP,
  TEXTURE_MAP_TO_TILING_TYPE,
  TILING_NAME_MAP,
  TILING_TEXTURE_MAP,
  TILING_TYPE,
  TILING_TYPE_LABEL,
} from '../../../../config/constant/unityConstants';
import { toUnityFixed } from '../../../../helpers/unityHelper';
import {
  selectIndividualProjectData,
  setCuratorLoader,
} from '../../../../redux/slicers/admin/curatorSlicer';
import {
  curatorStylesSelector,
  selectApplyTextureInProgress,
  selectIsSelectMapModeActive,
  selectSelectedTexture,
  selectSelectMapMode,
  setActiveTab,
  setApplyTextureInProgress,
  setPropertiesTabScrollTop,
  setSelectedTextureId,
  setSelectMapMode,
  TAB_NAME,
} from '../../../../redux/slicers/admin/curatorStylesSlicer';
import {
  clearTempObjectData,
  selectDataByTilingType,
  selectMainTilingType,
  selectParsedObject,
  selectTempUnitySelectedObjectInfo,
  selectUnitySelectedObjectInfo,
  selectUnitySelectedObjectInfoRaw,
  storeTempObjectData,
  updateRawObject,
  updateUnityObject,
} from '../../../../redux/slicers/admin/curatorUnityObjectSlicer';
import { getTexture, updateDbLowTexture } from '../../../../helpers/idb';
import { getAppState } from '../../../../redux/store';
import { errorToast } from '../../../../helpers/toastHelper';
import { base64ToBlob, getImageDetails, isValidNumber } from '../../../../helpers/jsHelper';
import { apiTextureIsMapCheck, apiUploadTextureMap } from '../../../../helpers/api';
import { UnityEvent, waitForEventOnce } from '../../../../helpers/unityPubSub';
import { undoRedo } from '../UndoRedo/UndoRedo';
import {
  selectGenerateFromDesignLoading,
  setGenerateFromDesignLoading,
} from '../../../../redux/slicers/admin/curatorLoaderSlicer';
import { GENERATED_NORMAL_MAP_FILTER_OPTION_ID } from '../../../../config/constant/curatorConstants';
import { getMapFilter } from './TexturesTab/useFilterOption';
import { filterSelector } from '../../../../redux/slicers/filters/filterSlicer';
import { paginatedTexturesActions } from '../../../../redux/slicers/textures/paginatedTexturesSlicer';
//import { captureException } from '@sentry/react';
import { useMapSelection } from './useMapSelection';
import { unitycontext } from '../../../../hooks/useUnityContext';
import { useContext } from 'react';
import { DIRTY_TYPE } from '../../../../pages/admin/curator/curatorState';
import { isConfiguratorView } from '../../../Configurator/utils';
import { LogCurator } from '../../../../helpers/logEvents';
import { convertActiveUnitToCM } from '../../../../helpers/curatorActions';
import { isUserOnboarding } from '../../../gamificationTooltip/helpers';

export const useUnityRedux = () => {
  const dispatch = useDispatch();
  const unity = useContext(unitycontext);
  const selectMapMode = useSelector((state) => curatorStylesSelector(state).selectMapMode);
  const { cancelMapSelection, setTempSelectedTexture, restoreScrollAfterCustomMap } =
    useMapSelection();
  const setDataNotSaved = unity.setDataNotSaved;
  const isOnboarding = isUserOnboarding();

  const isApplyTextureInProgress = () => {
    return selectApplyTextureInProgress(getAppState());
  };

  const applyTexture = async ({
    texture,
    mapType = TEXTURE_MAP.DIFFUSE,
    switchToProperties = false,
    skipHistory = false,
    showMessage = true,
    saveData = true
  }) => {
    if (isApplyTextureInProgress() && showMessage) {
      errorToast('Please wait, previous texture is still in progress', {
        toastId: 'TEXTURE_APPLY_IN_PROGRESS',
      });
      return false;
    }

    const tilingOptionType = TEXTURE_MAP_TO_TILING_TYPE[mapType];
    const prevTiling = getDataByTilingOptionType(tilingOptionType);

    // if (parseInt(prevTiling?.materialId) === parseInt(texture.id)) {
    //   const errorMessage =
    //     mapType === TEXTURE_MAP.DIFFUSE
    //       ? 'This texture is already applied'
    //       : 'This custom map is already applied';
    //   errorToast(errorMessage, { toastId: 'APPLY_TEXTURE_CUSTOM_MAP' });
    //   return false;
    // }

    dispatch(setApplyTextureInProgress(true));
    dispatch(setCuratorLoader(false));
    LogCurator('SAHA APPLY TEXTURE !!!', { texture, mapType });

    dispatch(setSelectedTextureId(parseInt(texture.id)));
    try {
      const result = await unity.materialModule.selectTexture({
        texture,
        mapType,
      });
      if (result === false) {
        throw new Error('unable to apply texture');
      }
    } catch (error) {
      console.error(error);
      //captureException(error);
      dispatch(setCuratorLoader(true));
      dispatch(setApplyTextureInProgress(false));
      errorToast('Texture is not available. Please contact support. LF_8993283');
      return;
    }

    if (switchToProperties) {
      dispatch(setActiveTab(TAB_NAME.PROPERTIES));
    }

    if (isOnboarding) {
      dispatch(setCuratorLoader(true));
    }

    if (!skipHistory) {
      undoRedo.objectProperties.applyTexture({
        prevTiling,
        nextTexture: texture,
        mapType,
      });
    }

    saveData && setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const getUpdateObject = (tilingOptionType, props) => {
    const parsedObject = selectParsedObject(getAppState());
    const key = TILING_NAME_MAP[tilingOptionType];
    const result = Object.entries(props).reduce((acc, [propKey, value]) => {
      if (propKey === 'width' || propKey === 'height') {
        if (typeof value === 'string' && value.trim() === '') {
          acc[propKey] = '';
          return acc;
        }
      }

      acc[propKey] = typeof value !== 'undefined' ? parseFloat(value) : parsedObject[key][propKey];
      return acc;
    }, {});

    return result;
  };

  const update = (tilingType, data) => {
    dispatch(
      updateUnityObject({
        [TILING_NAME_MAP[tilingType]]: data,
      })
    );
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const reset = async () => {
    const texture = getDataByTilingOptionType(TILING_TYPE.TEXTURE);
    const data = selectMainTilingType(getAppState());

    if (!texture?.mapImage && !texture?.materialId) return;

    let dbTexture;
    try {
      dbTexture = await getTexture(data.materialId);
    } catch (e) {
      //captureException(e);
      console.error(e);
    }

    let dbTextureWidth = dbTexture?.width;
    let dbTextureHeight = dbTexture?.height;
    if (dbTextureWidth) {
      dbTextureWidth = dbTextureWidth * CM_IN_ONE_INCHES;
    }
    if (dbTextureHeight) {
      dbTextureHeight = dbTextureHeight * CM_IN_ONE_INCHES;
    }

    let width = parseFloat(dbTextureWidth);
    let height = parseFloat(dbTextureHeight);

    if (!width || !height) {
      try {
        const imgUrl = dbTexture?.file || texture.mapImage;
        const imageDetails = await getImageDetails(imgUrl);
        width = imageDetails.width / imageDetails.dpi;
        height = imageDetails.height / imageDetails.dpi;
      } catch (e) {
        //captureException(e);
        console.error(e);
      }
    }

    if (data.width === width && data.height === height) {
      return;
    }

    const resetData = { width, height };

    changeSize({
      ...resetData,
      tilingOptionType: data.tilingType,
    });

    undoRedo.objectProperties.changeSize({
      prevValue: {
        width: data.width,
        height: data.height,
      },
      nextValue: resetData,
      data: {
        tilingOptionType: data.tilingType,
      },
    });

    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const changeSize = ({ width, height, tilingOptionType, count = 2 }) => {
    if (typeof height === 'number') {
      height = toUnityFixed(height, count);
    }

    if (typeof width === 'number') {
      width = toUnityFixed(width, count);
    }

    const upd = getUpdateObject(tilingOptionType, { width, height });

    unity.materialModule.OnUpdateTilingSizeReceiver({
      ...upd,
      tilingOptionType,
    });

    update(tilingOptionType, upd);
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const changePosition = ({ x, y, tilingOptionType, count = 2 }) => {
    const upd = getUpdateObject(tilingOptionType, {
      x,
      y,
    });

    upd.x = toUnityFixed(upd.x, count);
    upd.y = toUnityFixed(upd.y, count);

    unity.materialModule.OnUpdateTilingOffsetReceiver({
      ...upd,
      tilingOptionType,
    });

    update(tilingOptionType, upd);
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const changeRotation = ({ width, height, x, y, rotation, tilingOptionType }) => {
    const parsedObject = selectParsedObject(getAppState());
    const key = TILING_NAME_MAP[tilingOptionType];
    const currentRotation = parsedObject[key].rotation;
    const upd = getUpdateObject(tilingOptionType, {
      width,
      height,
      x,
      y,
      rotation,
    });

    if (
      !isValidNumber(upd.width) ||
      !isValidNumber(upd.height) ||
      !isValidNumber(upd.x) ||
      !isValidNumber(upd.y)
    ) {
      errorToast('Please provide data before rotating an object');
      return;
    }

    if (
      (currentRotation % 20 === 0 && rotation % 20 !== 0) ||
      (currentRotation % 20 !== 0 && rotation % 20 === 0)
    ) {
      // switch data
      const tempUpd = { ...upd };
      upd.width = tempUpd.height;
      upd.height = tempUpd.width;
      upd.x = tempUpd.y;
      upd.y = tempUpd.x;
    }

    unity.materialModule.OnUpdateRotationReceiver({
      ...upd,
      tilingOptionType,
    });

    update(tilingOptionType, upd);
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
    return upd;
  };

  const setFit = (tilingType) => {
    unity.materialModule.OnSetFitReceiver(tilingType);
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const setLock = (tilingType, value) => {
    LogCurator('set lock ', tilingType, value);
    unity.materialModule.OnLockTilingReceiver(tilingType);
    dispatch(
      updateUnityObject({
        [TILING_NAME_MAP[tilingType]]: {
          lock: value,
        },
      })
    );
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const glossiness = {
    changeIntensity: unity.materialModule.OnUpdateRoughnessValueReceiver,
    invertMap: unity.materialModule.InvertRoughnessToggleReceiver,
    useCustomMap: unity.materialModule.UseCustomRoughnessMapToggleReceiver,
  };

  const normal = {
    changeIntensity: unity.materialModule.OnUpdateNormalValueReceiver,
    invertMap: unity.materialModule.InvertNormalToggleReceiver,
    useCustomMap: unity.materialModule.UseCustomNormalMapToggleReceiver,
    // gereateFromDesign: unity.materialModule.UseGenerateNormalMapToggleReceiver,
  };

  const transparency = {
    changeIntensity: unity.materialModule.OnUpdateTransparencyValueReceiver,
    invertMap: unity.materialModule.InvertTransparencyToggleReceiver,
    useCustomMap: unity.materialModule.UseCustomTransparencyMapToggleReceiver,
    textureAlpha: unity.materialModule.SetTransparencyFromDesignReceiver,
  };

  const ao = {
    changeIntensity: unity.materialModule.OnUpdateAoValueReceiver,
    useCustomMap: unity.materialModule.UseCustomAOMapToggleReceiver,
    invertMap: unity.materialModule.InvertAoToggleReceiver,
  };

  const metallic = {
    changeIntensity: unity.materialModule.OnUpdateMetallicValueReceiver,
    useCustomMap: unity.materialModule.UseCustomMetallicMapToggleReceiver,
    invertMap: unity.materialModule.InvertMetallicToggleReceiver,
  };

  const emission = {
    changeIntensity: unity.materialModule.OnUpdateEmissionValueReceiver,
  };

  const clearcoat = {
    changeIntensity: unity.materialModule.OnUpdateClearCoatValueReceiver,
  };

  const updaters = {
    [TILING_TYPE.GLOSSY]: glossiness,
    [TILING_TYPE.DEPTH]: normal,
    [TILING_TYPE.TRANSPARENCY]: transparency,
    [TILING_TYPE.AO]: ao,
    [TILING_TYPE.METALLIC]: metallic,
    [TILING_TYPE.EMISSION]: emission,
    [TILING_TYPE.CLEARCOAT]: clearcoat,
  };

  const changeIntensity = (tilingOptionType, intensity) => {
    const label = TILING_TYPE_LABEL[tilingOptionType];
    const parsedObject = selectParsedObject(getAppState());

    if (!parsedObject) return;
    const { normal, ao } = parsedObject;

    if (tilingOptionType === TILING_TYPE.AO && !ao.applied) {
      errorToast(`Please add a custom ${label} map`, {
        toastId: 'ADD_CUSTOM_MAP_AO_ERROR',
      });
      return;
    }

    if (tilingOptionType === TILING_TYPE.DEPTH && !normal.applied) {
      errorToast(`Please add a custom ${label} map`, {
        toastId: 'ADD_CUSTOM_MAP_DEPTH_ERROR',
      });
      return;
    }

    updaters[tilingOptionType].changeIntensity(intensity);
    update(tilingOptionType, { intensity });
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const toggleCustomMap = (tilingOptionType, active, { skipHistory = false } = {}) => {
    LogCurator('Curator', 'sasha toggleCustomMap');

    if (!skipHistory) {
      undoRedo.objectProperties.disableCustomMap({
        tilingOptionType,
        prevValue: !active,
        nextValue: active,
      });
    }

    updaters[tilingOptionType].useCustomMap(active);
    update(tilingOptionType, {
      customMap: active,
      ...(tilingOptionType === TILING_TYPE.DEPTH && {
        gereateFromDesign: false,
        intensity: 0,
      }),
      ...(tilingOptionType === TILING_TYPE.TRANSPARENCY && {
        textureAlpha: false,
      }),
      ...(!active && { mapImage: null, hasMapImage: false, materialId: null }),
    });

    if (!active) {
      // toggleSeparateTiling(tilingOptionType, false);

      const t = TILING_NAME_MAP[tilingOptionType];
      const idKey = CHANGED_MAP_PATHS_MAP[t];
      if (idKey) {
        dispatch(
          updateRawObject({
            changedMapsPath: {
              [idKey]: '',
            },
          })
        );
      }
    }
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const getDataByTilingOptionType = (tilingOptionType) => {
    return selectDataByTilingType(getAppState(), tilingOptionType);
  };

  const toggleInvertMap = (tilingOptionType, { skipHistory = false } = {}) => {
    const data = getDataByTilingOptionType(tilingOptionType);

    if (!data) return;

    const active = !data.invertMap;
    updaters[tilingOptionType].invertMap(active);
    update(tilingOptionType, { invertMap: active });

    if (!skipHistory) {
      undoRedo.objectProperties.updateObject({
        name: 'invertMap',
        prevValue: data.invertMap,
        nextValue: active,
        data: { tilingOptionType },
      });
    }

    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const toggleSeparateTiling = (tilingOptionType, active) => {
    unity.materialModule.AddRemoveSeparateTilingOptionReceiver({
      status: active,
      tilingOptionType,
    });
    // redux data will be updated on unity callback also Mat37 -> ReceiveSeparateTilingProperties
    update(tilingOptionType, { useSeparateTiling: active });
    // setLock(tilingOptionType, true);
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  // const toggleGenerateFromDesign = (active) => {
  //   LogCurator("Curator",'sasha toggleGenerateFromDesign')
  //   normal.gereateFromDesign(active)
  //   update(TILING_TYPE.DEPTH, { gereateFromDesign: active })
  // }

  const toggleTextureAlpha = (active) => {
    transparency.textureAlpha(active);
    update(TILING_TYPE.TRANSPARENCY, { textureAlpha: active });
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const toggleEffectLightColorBounce = (active) => {
    LogCurator('Curator', 'sasha toggleEffectLightColorBounce');
    unity.materialModule.OnEffectLightColorBounceEnableDisableReceiver(active);
    update(TILING_TYPE.OTHER, { effectLightColorBounce: active });
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const toggleUseSpecularCalculation = (active) => {
    LogCurator('Curator', 'sasha toggleUseSpecularCalculation');
    unity.materialModule.UseSpecularCalculationReceiver(active);
    update(TILING_TYPE.OTHER, { useSpecularCalculation: active });
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const toggleMapSelection = (tilingOptionType) => {
    dispatch(setSelectMapMode(tilingOptionType));

    // filter textures by selected map
    const { filterData } = filterSelector(getAppState());
    const filter = getMapFilter(tilingOptionType, filterData?.results);
    const filterOptionValue = `option_${filter.filter_label}_${filter.id}`;
    dispatch(paginatedTexturesActions.resetFilters());
    dispatch(paginatedTexturesActions.toggleFilter(filterOptionValue));

    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });

    // save scroll
    const element = document.getElementById('CuratorSidebarLayoutScrollableContainer_style');
    if (element) {
      dispatch(setPropertiesTabScrollTop(element.scrollTop));
    }
  };

  const applyCustomMapAction = (texture) => {
    if (isApplyTextureInProgress()) {
      errorToast('Please wait, texture or custom map is still in progress');
      return;
    }

    const tempObject = selectTempUnitySelectedObjectInfo(getAppState());
    if (!tempObject) {
      dispatch(storeTempObjectData()); // store object for later if user decides to cancel action without applying map -> we need revert applied maps
    }

    const mapType = TILING_TEXTURE_MAP[selectMapMode];
    // toggleMapSelection(null);

    applyTexture({
      texture,
      mapType,
      skipHistory: true,
    });

    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const confirmApplyCustomMap = async () => {
    try {
      const prevData = selectTempUnitySelectedObjectInfo(getAppState());
      const selectMapMode = selectSelectMapMode(getAppState());
      const tilingType = selectMapMode;
      const textureMapType = TILING_TEXTURE_MAP[tilingType];
      // const tilingOptionType = TEXTURE_MAP_TO_TILING_TYPE[selectMapMode];
      const currentTiling = getDataByTilingOptionType(selectMapMode);
      const textureId = currentTiling.materialId;
      const texture = await getTexture(textureId);
      const key = TILING_NAME_MAP[selectMapMode];
      const prevTiling = prevData.parsed[key];

      undoRedo.objectProperties.applyTexture({
        prevTiling,
        nextTexture: texture,
        mapType: textureMapType,
        parsedObject: prevData.parsed,
        rawObject: prevData.raw,
      });
    } catch (error) {
      //captureException(error);
      console.error(error);
      errorToast('Error happened while applying custom map');
      cancelMapSelection();
    }

    dispatch(clearTempObjectData());
    dispatch(setSelectMapMode(null));
    dispatch(setActiveTab(TAB_NAME.PROPERTIES));
    dispatch(paginatedTexturesActions.resetFilters());
    setTempSelectedTexture(null);

    restoreScrollAfterCustomMap();
  };

  const generateFromDesign = async (tilingOptionType, active) => {
    if (selectGenerateFromDesignLoading(getAppState())) {
      errorToast('Please wait, previous action is still in progress');
      return;
    }

    const normalData = getDataByTilingOptionType(tilingOptionType);
    const textureData = getDataByTilingOptionType(TILING_TYPE.TEXTURE);
    const projectData = selectIndividualProjectData(getAppState());
    const mapType = TILING_TEXTURE_MAP[tilingOptionType];

    if (!active) {
      const rawObject = selectUnitySelectedObjectInfoRaw(getAppState());
      const generateFromDesignVisible = rawObject.isTexture && rawObject.isTextureChanged;

      undoRedo.objectProperties.disableGenerateFromDesign({ mapType }); // undo/redo

      await unity.materialModule.UseGenerateNormalMapToggleReceiver({
        id: normalData.mapId,
        active,
      });
      update(TILING_TYPE.DEPTH, {
        generateFromDesign: active,
        mapImage: null,
        generateFromDesignVisible,
        hasMapImage: false,
      });
      setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
      return;
    }

    setGenerateFromDesignLoading(true);
    // TODO: add check in index db before sending request
    const result = await apiTextureIsMapCheck({
      textureId: textureData.materialId,
    });

    if (result?.data) {
      undoRedo.objectProperties.generateFromDesign({
        texture: result.data,
        mapType,
      }); // undo/redo

      await unity.materialModule.selectLowTexture({
        texture: result.data,
        mapType,
        isGeneratedMap: true,
        isConfiguratorView: isConfiguratorView(),
      });
      setGenerateFromDesignLoading(false);
      setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
      return;
    }

    // map is missing -> generate new map
    const type = GENERATE_MAP_TYPE[tilingOptionType];
    const unityImage = await waitForEventOnce(UnityEvent.ReceiveBase64StringGenerateMap, {
      triggerFunc: () => unity.materialModule.GenerateMapReceiver(type),
    });

    const textureBlob = base64ToBlob({
      imageString: unityImage.imageInfo,
    });

    const texture = await getTexture(textureData.materialId);
    let widthInches, heightInches;

    try {
      const result = await getImageDetails(textureBlob);
      widthInches = result.widthInches;
      heightInches = result.heightInches;
    } catch (e) {
      //captureException(e);
      console.error(e);
      widthInches = 12;
      heightInches = 12;
    }

    // TODO: we need here width/height???
    const uploadResult = await apiUploadTextureMap({
      file: textureBlob,
      filterOptions: GENERATED_NORMAL_MAP_FILTER_OPTION_ID, // we support only normal maps for now
      name: `${texture.name}_normal`,
      originalTextureId: textureData.materialId,
      project: projectData?.id,
      width: toUnityFixed(widthInches),
      height: toUnityFixed(heightInches),
    });

    if (uploadResult?.data?.id) {
      // TODO: store data in indexdb
      const lowTexture = await updateDbLowTexture(uploadResult.data, {
        IS_GENERATED_MAP: true,
      });

      undoRedo.objectProperties.generateFromDesign({
        texture: lowTexture,
        mapType,
      }); // undo/redo

      await unity.materialModule.UseGenerateNormalMapToggleReceiver({
        id: uploadResult.data.id,
        active,
        texture: uploadResult.data,
      });
      update(TILING_TYPE.DEPTH, {
        generateFromDesign: active,
        mapImage: lowTexture.file || lowTexture.low_texture_file,
      });
    } else {
      errorToast('Unable to upload generated map, please try again');
    }

    update(TILING_TYPE.DEPTH, { gereateFromDesign: active });
    setGenerateFromDesignLoading(false);
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const handleWidthHeightChange = (tilingOptionType, { width, height }) => {
    changeSize({
      tilingOptionType,
      width: convertActiveUnitToCM(width),
      height: convertActiveUnitToCM(height),
    });
    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const modifyTiling = (tilingOptionType, name, value, { skipHistory } = {}) => {
    switch (name) {
      case 'x':
      case 'y':
        changePosition({ [name]: Number(value), tilingOptionType });
        break;
      case 'width':
      case 'height':
        changeSize({ [name]: Number(value), tilingOptionType });
        break;
      case 'intensity':
        changeIntensity(tilingOptionType, Number(value));
        break;
      case 'invertMap':
        toggleInvertMap(tilingOptionType, { skipHistory });
        break;
      case 'customMap':
        if (value) {
          toggleMapSelection(tilingOptionType);
          break;
        }
        toggleCustomMap(tilingOptionType, value);
        break;
      case 'lock':
        setLock(tilingOptionType, value);
        break;
      case 'useSeparateTiling':
        toggleSeparateTiling(tilingOptionType, value);
        break;
      case 'textureAlpha':
        toggleTextureAlpha(value);
        break;
      case 'effectLightColorBounce':
        toggleEffectLightColorBounce(value);
        break;
      case 'useSpecularCalculation':
        toggleUseSpecularCalculation(value);
        break;
      case 'generateFromDesign':
        generateFromDesign(tilingOptionType, value);
        break;

      case 'rotation':
        changeRotation({ [name]: parseInt(value), tilingOptionType });
        break;

      default:
        throw new Error('Property change handler is missing');
    }

    setDataNotSaved({ type: DIRTY_TYPE.MATERIAL_PROPERTIES });
  };

  const onChange = (tilingOptionType) => (event) => {
    let { name, value, type, checked } = event.target;
    value = type === 'checkbox' ? checked : value;
    if (TILING_TYPE.EMISSION === tilingOptionType) {
      modifyTiling(tilingOptionType, name, +value > EMISSION_NEW_MAX ? EMISSION_NEW_MAX : value);
    } else {
      modifyTiling(tilingOptionType, name, value);
    }
  };

  window.changeRotation = changeRotation;

  const selectCustomMapTexture = (texture) => {
    applyCustomMapAction(texture);
    setTempSelectedTexture(texture);
  };

  const handleTextureClick = (texture) => {
    const isSelectMapMode = selectIsSelectMapModeActive(getAppState());

    if (isSelectMapMode) {
      selectCustomMapTexture(texture);
    } else {
      applyTexture({ texture });
    }
  };

  return {
    applyTexture,
    setTempSelectedTexture,
    handleTextureClick,
    selectCustomMapTexture,

    changePosition,
    changeRotation,
    setFit,
    setLock,
    changeSize,
    onChange,
    reset,
    toggleMapSelection,
    applyCustomMapAction,
    cancelMapSelection,
    handleWidthHeightChange,
    modifyTiling,
    toggleCustomMap,
    confirmApplyCustomMap,
    generateFromDesign,
  };
};

export const useProperties = () => {
  const { parsed, raw } = useSelector(selectUnitySelectedObjectInfo);

  let texture = useSelector(selectSelectedTexture);

  // if (properties?.textureId !== texture?.id) {
  //   texture = null
  // }

  return {
    objectName: raw?.objectName,
    properties: raw,
    propertiesData: parsed,
    textureThumbnail: texture?.thumbnail,
    selectedTextureId: texture?.id,
    texture,
  };
};
